import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Grid,
  FormControlLabel,
  Radio,
  FormHelperText,
  MenuItem,
  Select,
  InputAdornment,
} from "@mui/material";
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import { styled } from "@mui/system";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  editUserPreference,
  getMasterData,
  getUserDetails,
  logoutClear,
  updateUserPreference,
} from "../../redux/actions/appActions";
import {
  SET_CITIES,
  SET_FUNCTIONS,
  SET_INDUSTRIES,
  SET_JOB_ROLES,
  SET_USER_PREFERENCE,
} from "../../redux/actions/actionTypes";
import MultiSelect from "../common/MultiSelect";
import { getKeyValuePairs } from "../../redux/services/utils";
import { CssSelectTextField } from "../common/CssTextField";
import { ROUTE_COMPLETE_PROFILE, ROUTE_GREAT_START, ROUTE_MAIN } from "../../constants/routes";
import { RootState } from "../../redux/types/reduxState";
import { JOB_TYPES, WORK_MODES } from "../../constants/AppVarConstants";
import API_MIDDLEWARE from "../../redux/api/middleware";
import { GET_MASTER_DATA } from "../../redux/api/config";

const Header = styled("header")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "left",
  padding: "20px",
  backgroundColor: "rgb(255, 137, 96)",
}));

const Logo = styled("img")({
  aspectRatio: "1.89",
  width: "75px",
});

const Avatar1 = styled(Avatar)({
  width: "51px",
  height: "51px",
});

const JobType = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "16px",
}));

const StyledTextBox = styled(Box)({
  fontSize: "16px",
  backgroundColor: "#ffffff",
  width: "303px",
  borderRadius: "10px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: "400",
  color: "#1F2235",
  lineHeight: "1.5",
  marginLeft: "10px",
});

const StyledTextBox1 = styled(Box)({
  fontSize: "16px",
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: "400",
  color: "#1F2235",
  lineHeight: "1.5",
});

type FormValues = Yup.InferType<any>;

const schema = Yup.object().shape({
  type: Yup.string(),
  jobRole: Yup.array().min(1, "Please select at least one job role"),
  industry: Yup.array().min(1, "Please select industry"),
  function: Yup.array().min(1, "Please select function"),
  jobType: Yup.array().min(1, "Please select job type"),
  workMode: Yup.array().min(1, "Please select work mode"),
  location: Yup.array().min(1, "Please enter or select at least one location"),
  minSal: Yup.number().typeError("Min salary is required").min(0, "Min salary must be greater than or equal to 0"),
  maxSal: Yup.number()
    .typeError("Max salary is required")
    .min(Yup.ref("minSal"), "Max salary must be greater than min salary"),
});

const defaultValues = {
  type: "",
  jobRole: [],
  industry: [],
  function: [],
  jobType: [],
  workMode: [],
  location: [],
  minSal: "",
  maxSal: "",
};

const jobTypes = ["Internship", "Job"];

const LookingFor = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userPreference = useSelector((state: RootState) => state.app.userPreference);
  const { industries, jobRoles, cities, functions } = useSelector((state: any) => state.app);
  const { industry, jobType, workMode, minSalary, maxSalary, city, jobRole, jobCategory, functionalArea } =
    userPreference || {};

  // Per-function job roles: fetch job roles for each selected function so we know which roles belong to which function
  const [rolesByFunctionState, setRolesByFunctionState] = useState<Record<string, string[]>>({});
  const [jobRoleOptionsFromFetch, setJobRoleOptionsFromFetch] = useState<Array<{ label: string; value: string }>>([]);
  const [loadingJobRolesByFunction, setLoadingJobRolesByFunction] = useState(false);

  const Logout = () => {
    dispatch(logoutClear(() => {
      navigate(ROUTE_MAIN, { replace: true });
    }));
  };

  useEffect(() => {
    dispatch(getMasterData("INDUSTRY", SET_INDUSTRIES));
    dispatch(getMasterData("FUNCTIONS", SET_FUNCTIONS));
    dispatch(getMasterData("CITY", SET_CITIES));
    dispatch(getUserDetails(SET_USER_PREFERENCE, "preferences"));
  }, []);

  const { handleSubmit, control, watch, setValue, getValues, setError, clearErrors } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver<FormValues>(schema),
    reValidateMode: "onChange",
  });

  const watchFunction = watch("function");

  // Build job role options and map of functionId -> role values (when API returns objects with parentId/parent_id/functionId)
  const { jobRoleOptions, rolesByFunction } = useMemo(() => {
    if (!jobRoles?.length) return { jobRoleOptions: [], rolesByFunction: null as Record<string, string[]> | null };
    const first = jobRoles[0];
    type RoleItem = { name?: string; id?: string; value?: string; label?: string; parentId?: string; parent_id?: string; functionId?: string };
    const getParentId = (r: RoleItem): string =>
      (r.parentId ?? r.parent_id ?? r.functionId ?? "") as string;
    const isObjectWithParent =
      typeof first === "object" &&
      first !== null &&
      (getParentId(first as RoleItem) !== "" || "parentId" in first || "parent_id" in first || "functionId" in first);
    const hasValidParent = isObjectWithParent && getParentId(first as RoleItem) !== "";
    if (!hasValidParent) {
      return { jobRoleOptions: getKeyValuePairs(jobRoles as string[]), rolesByFunction: null };
    }
    const roles = jobRoles as RoleItem[];
    const options = roles.map((r) => ({
      label: r.name ?? r.label ?? r.value ?? String(r.id ?? ""),
      value: (r.id ?? r.value ?? r.name ?? "") as string,
    }));
    const byFunction: Record<string, string[]> = {};
    roles.forEach((r) => {
      const pid = getParentId(r);
      const val = String(r.id ?? r.value ?? r.name ?? "");
      if (pid) {
        if (!byFunction[pid]) byFunction[pid] = [];
        byFunction[pid].push(val);
      }
    });
    return { jobRoleOptions: options, rolesByFunction: byFunction };
  }, [jobRoles]);

  // Effective job role options: prefer per-function fetch result so we show roles for all selected functions
  const effectiveJobRoleOptions =
    jobRoleOptionsFromFetch.length > 0
      ? jobRoleOptionsFromFetch
      : jobRoleOptions.length
        ? jobRoleOptions
        : getKeyValuePairs(jobRoles);

  // When preferred function is removed, clear only job roles that belonged to that function (no longer in options list)
  useEffect(() => {
    if (!effectiveJobRoleOptions?.length) return;
    const currentJobRole = getValues("jobRole");
    if (!currentJobRole?.length) return;
    const roleValues = effectiveJobRoleOptions.map((o: { label: string; value: string }) => o.value);
    const filtered = currentJobRole.filter((ro: { value: string }) => roleValues.includes(ro.value));
    if (filtered.length < currentJobRole.length) {
      setValue("jobRole", filtered);
    }
  }, [effectiveJobRoleOptions]);

  useEffect(() => {
    if (userPreference && Object.keys(userPreference).length > 0 && userPreference.maxSalary) {
      setValue("type", jobCategory);
      setValue("function", getKeyValuePairs(functionalArea));
      setValue("industry", getKeyValuePairs(industry));
      setValue("jobType", getKeyValuePairs(jobType));
      setValue("workMode", getKeyValuePairs(workMode));
      if (city && city.length > 0) {
        setValue("location", getKeyValuePairs(city));
      }
      setValue("minSal", minSalary);
      setValue("maxSal", maxSalary);
      setValue("jobRole", getKeyValuePairs(jobRole));
    }
  }, [userPreference]);

  useEffect(() => {
    if (watchFunction?.length) {
      // Check if all selected functions are from the predefined list
      const allFunctionsFromList = watchFunction.every((selectedFunc: any) =>
        functions.some((func: string) => func === selectedFunc.value)
      );

      if (allFunctionsFromList) {
        // If all functions are from the list, fetch filtered job roles (combined for dropdown in Redux)
        dispatch(
          getMasterData("JOB_ROLES", SET_JOB_ROLES, `parentId=${watchFunction.map((el: any) => el.value).join(",")}`)
        );
      } else {
        // If any function is custom, fetch all job roles
        dispatch(getMasterData("JOB_ROLES", SET_JOB_ROLES));
      }
    }
  }, [watchFunction, functions]);

  // Fetch job roles per function to build function -> roles mapping for validation
  useEffect(() => {
    if (!watchFunction?.length) {
      setRolesByFunctionState({});
      setJobRoleOptionsFromFetch([]);
      return;
    }
    const allFromList = watchFunction.every((selectedFunc: { value: string }) =>
      functions.some((func: string) => func === selectedFunc.value)
    );
    if (!allFromList) {
      setRolesByFunctionState({});
      setJobRoleOptionsFromFetch([]);
      return;
    }
    setLoadingJobRolesByFunction(true);
    const fetchPromises = watchFunction.map((f: { value: string }) =>
      API_MIDDLEWARE(
        GET_MASTER_DATA("JOB_ROLES", `parentId=${encodeURIComponent(f.value)}`),
        "GET",
        { "Content-Type": "application/json" }
      )
    );
    Promise.all(fetchPromises)
      .then((responses: any[]) => {
        const byFunction: Record<string, string[]> = {};
        const allRoleValues = new Set<string>();
        watchFunction.forEach((f: { value: string }, idx: number) => {
          const res = responses[idx];
          const roles = res?.result ?? res ?? [];
          const roleValues: string[] = Array.isArray(roles)
            ? roles.map((r: string | { name?: string; id?: string; value?: string }) =>
                typeof r === "string" ? r : String((r as { name?: string; id?: string; value?: string }).id ?? (r as { name?: string; id?: string; value?: string }).value ?? (r as { name?: string; id?: string; value?: string }).name ?? "")
              )
            : [];
          byFunction[f.value] = roleValues;
          roleValues.forEach((v) => allRoleValues.add(v));
        });
        setRolesByFunctionState(byFunction);
        setJobRoleOptionsFromFetch(Array.from(allRoleValues).map((v) => ({ label: v, value: v })));
      })
      .catch(() => {
        setRolesByFunctionState({});
        setJobRoleOptionsFromFetch([]);
      })
      .finally(() => setLoadingJobRolesByFunction(false));
  }, [watchFunction, functions]);

  const onSubmit = (data: FormValues) => {
    clearErrors("jobRole");
    const selectedFunctions = data?.function ?? [];
    const selectedJobRoles = data?.jobRole ?? [];
    const functionCount = Array.isArray(selectedFunctions) ? selectedFunctions.length : 0;
    const jobRoleCount = Array.isArray(selectedJobRoles) ? selectedJobRoles.length : 0;

    if (functionCount === 0) {
      // No functions selected - schema will handle other validations
    } else {
      // Use per-function mapping for validation (from our fetch or from API parentId in jobRoles)
      const mappingToUse =
        Object.keys(rolesByFunctionState).length > 0 ? rolesByFunctionState : rolesByFunction;
      const functionValues = selectedFunctions.map((f: { value: string }) => f?.value);
      const jobRoleValues = selectedJobRoles.map((r: { value: string }) => r?.value);
      const functionsMissingJobRole: string[] = [];

      if (mappingToUse) {
        // Validate each function has at least one job role that belongs to that function
        functionValues.forEach((funcValue: string) => {
          const rolesForFunc = mappingToUse[funcValue] ?? [];
          const hasRoleForFunc = jobRoleValues.some((roleVal: string) => rolesForFunc.includes(roleVal));
          if (!hasRoleForFunc) {
            const funcLabel = selectedFunctions.find((f: { value: string }) => f?.value === funcValue);
            functionsMissingJobRole.push((funcLabel as { label?: string })?.label ?? funcValue);
          }
        });
      } else {
        // No mapping: fall back to count (at least one job role per function by count)
        if (jobRoleCount < functionCount) {
          (selectedFunctions as Array<{ label?: string; value?: string }>).forEach((f) => {
            functionsMissingJobRole.push(f?.label ?? f?.value ?? "");
          });
        }
      }

      if (functionsMissingJobRole.length > 0) {
        setError("jobRole", {
          type: "manual",
          message: `Please select at least one job role for each function.`,
        });
        return;
      }
    }

    const payload = {
      jobCategory: data?.type,
      jobRole: selectedJobRoles.map((el: any) => el?.value),
      industry: data?.industry?.map?.((el: any) => el?.value),
      jobType: data?.jobType?.map?.((el: any) => el?.value),
      workMode: data?.workMode?.map?.((el: any) => el?.value),
      city: data?.location?.map?.((el: any) => el?.value),
      minSalary: data?.minSal,
      maxSalary: data?.maxSal,
      functionalArea: selectedFunctions.map((el: any) => el?.value),
    };
    if (userPreference?.jobCategory) {
      dispatch(
        editUserPreference(payload, () =>
          navigate(location.search.includes("edit") ? `${ROUTE_COMPLETE_PROFILE}?tab=1` : ROUTE_GREAT_START)
        )
      );
    } else {
      dispatch(
        updateUserPreference(payload, () =>
          navigate(location.search.includes("edit") ? `${ROUTE_COMPLETE_PROFILE}?tab=1` : ROUTE_GREAT_START)
        )
      );
    }
  };

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" bgcolor="#ffffff" width="100%">
      <Box
        sx={{
          display: "flex",
          backgroundColor: "rgb(255, 137, 96)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Header onClick={() => navigate(ROUTE_COMPLETE_PROFILE)}>
          <Logo src="/images/client-website/seevi-logo.png" alt="Logo" />
        </Header>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box sx={{ mt: 0, mr: 1 }} onClick={() => navigate(ROUTE_COMPLETE_PROFILE)} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50 }}>
              <HomeIcon />
            </InputAdornment>
          </Box>
          <Box sx={{ mt: 0, mr: 2 }} onClick={() => Logout()} style={{ cursor: "pointer" }}>
            <InputAdornment position="end" style={{ color: "#fff", width: 20, height: 50 }}>
              <LogoutIcon />
            </InputAdornment>
          </Box>
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" alignItems="left" fontSize="14px" fontWeight="500" color="#757575">
        <Box display="flex" alignItems="center" padding="5px 16px" sx={{ backgroundColor: "#eee" }}>
          <Avatar1 src="/images/avatar-one.png" alt="Avatar" />
          <StyledTextBox p={1} my={1}>
            <Box>
              <Typography
                style={{
                  fontFamily: '"DM Sans", sans-serif !important',
                  fontSize: "14px !important",
                  fontWeight: "medium !important",
                  color: "#1F2235",
                }}
              >
                2/2
              </Typography>
              <Typography
                style={{
                  fontFamily: '"DM Sans", sans-serif !important',
                  fontSize: "14px !important",
                  fontWeight: "medium !important",
                  color: "#1F2235",
                }}
              >
                What are you looking for?
              </Typography>
            </Box>
          </StyledTextBox>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", backgroundColor: "#eee", py: 1 }}>
          <StyledTextBox1 mx={2} px={2} py={1}>
            Filling this will help us recommend relevant jobs for you.
          </StyledTextBox1>
        </Box>
      </Box>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        display="flex"
        flexDirection="column"
        p={2}
        bgcolor="#ffffff"
      >
        <Grid container spacing={3} pl={3}>
          <Controller
            name="type"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Box sx={{ display: "flex", gap: 5, mt: 3 }}>
                {jobTypes.map((jobType) => (
                  <Grid item xs={4} key={jobType}>
                    <FormControlLabel
                      control={
                        <Radio
                          value={jobType}
                          onChange={(e) => {
                            const newType = e.target.value;
                            if (newType !== value) {
                              setValue("minSal", "");
                              setValue("maxSal", "");
                            }
                            onChange(e.target.value);
                          }}
                          checked={value === jobType}
                        />
                      }
                      label={
                        <JobType>
                          <Typography>{jobType}</Typography>
                        </JobType>
                      }
                    />
                  </Grid>
                ))}
                <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>{error ? error?.message : " "}</FormHelperText>
              </Box>
            )}
          />
        </Grid>

        <Typography mt={2}>Preferred Functions</Typography>
        <MultiSelect name="function" control={control} options={getKeyValuePairs(functions)} />
        <Typography mt={2}>Preferred Job Role</Typography>
        <MultiSelect
          disabled={!watchFunction?.length || loadingJobRolesByFunction}
          name="jobRole"
          control={control}
          options={effectiveJobRoleOptions}
        />
        <Typography mt={2}>Preferred Industry</Typography>
        <MultiSelect name="industry" control={control} options={getKeyValuePairs(industries)} />
        <Typography mt={2}>Preferred Job Type</Typography>
        <MultiSelect name="jobType" control={control} options={getKeyValuePairs(JOB_TYPES)} />
        <Typography mt={2}>Preferred Work Mode</Typography>
        <MultiSelect name="workMode" control={control} options={getKeyValuePairs(WORK_MODES)} />
        <Typography mt={2}>
          {watch("type") === "Internship" ? "Stipend" : "Salary"} Range Expected Per{" "}
          {watch("type") === "Internship" ? "Month" : "Annum"}
        </Typography>
        <Grid container spacing={2} mt={-1}>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="minSal"
              render={({ field, fieldState: { error } }) => (
                <>
                  <CssSelectTextField fullWidth error={!!error}>
                    <Select
                      sx={{
                        borderRadius: "99px",
                        boxShadow: "inset 0 0 11px 2px #dfd9d9",
                        "& .MuiSelect-select": {
                          padding: "12.3px 16px",
                        },
                        mt: 1,
                      }}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={field?.value}
                      error={!!error}
                      onChange={field.onChange}
                      displayEmpty={true}
                      renderValue={(value) => (value ? value : "Minimum")}
                    >
                      {Array.from({ length: 50 }).map((_, index) => {
                        const minSal = watch("type") === "Internship" ? (index + 1) * 1000 : (index + 1) * 100000;
                        return (
                          <MenuItem key={minSal} value={minSal}>
                            {minSal.toLocaleString()}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </CssSelectTextField>
                  <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                    {error ? error?.message : " "}
                  </FormHelperText>
                </>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="maxSal"
              render={({ field, fieldState: { error } }) => (
                <>
                  <CssSelectTextField fullWidth error={!!error}>
                    <Select
                      sx={{
                        borderRadius: "99px",
                        boxShadow: "inset 0 0 11px 2px #dfd9d9",
                        "& .MuiSelect-select": {
                          padding: "12.3px 16px",
                        },
                        mt: 1,
                      }}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={field?.value}
                      error={!!error}
                      onChange={field.onChange}
                      displayEmpty={true}
                      renderValue={(value) => (value ? value : "Maximum")}
                    >
                      {Array.from({ length: 50 }).map((_, index) => {
                        const minSal = watch("type") === "Internship" ? (index + 1) * 1000 : (index + 1) * 100000;
                        return (
                          <MenuItem key={minSal} value={minSal}>
                            {minSal.toLocaleString()}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </CssSelectTextField>
                  <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: 2 }}>
                    {error ? error?.message : " "}
                  </FormHelperText>
                </>
              )}
            />
          </Grid>
        </Grid>
        <Typography mt={2}>Preferred Location</Typography>
        <MultiSelect name="location" control={control} options={getKeyValuePairs(cities)} freesolo />
        {location?.search?.includes("edit") ? (
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              justifyContent: "center",
              alignItems: "center",
              padding: "15px 64px",
              marginTop: "20px",
              marginBottom: "15px",
              fontSize: "16px",
              fontWeight: 600,
              color: "#ffffff",
              backgroundColor: "#756DE9",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              borderRadius: "95px",
              textTransform: "none",
            }}
          >
            Save
          </Button>
        ) : (
          <Box display="flex">
            <Button
              sx={{
                backgroundColor: "white", // Change background color to white
                borderRadius: "99px",
                fontSize: "16px",
                textTransform: "capitalize",
                width: "27%",
                color: "#000000",
                "&:hover ": {
                  backgroundColor: "#f5f5f5", // Change hover background color if needed
                },
              }}
              variant="contained"
              size="small"
              style={{ padding: "15px 40px" }}
              className="w-100 mt-2 btn-pad"
              onClick={() => navigate(-1)}
            >
              Back
            </Button>

            <Box flexGrow={1}></Box>
            <Button
              type="submit"
              sx={{
                backgroundColor: "#756DE9",
                borderRadius: "99px",
                fontSize: "16px",
                textTransform: "capitalize",
                width: "70%",
                paddingY: "15px",
                paddingX: 0,
                "&:hover": {
                  backgroundColor: "#756DE9",
                },
              }}
              variant="contained"
              size="small"
              className="w-100 mt-2 btn-pad"
            >
              Save and Proceed
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LookingFor;
